<template>
  <div>
    <DsSection
      v-for="(section, index) in sections"
      :key="section.id || section.title"
      :with-space="false"
      :theme="section.theme || 'light'"
      class="section-paddings"
      :full-screen="
        section.contentType === 'BlockCampaignsMedia' ||
        section.contentType === 'BlockCampaignsCards' ||
        section.contentType === 'BlockLogoBar' ||
        section.contentType === 'BlockLandingLogoBar'
      "
      :class="{
        'same-theme-above': sectionThemes[index].sameAbove,
        'same-theme-below': sectionThemes[index].sameBelow
      }"
    >
      <BlockUspList
        v-if="section.contentType === 'BlockLandingUspList'"
        :items="section.items"
        :title="section.title"
        :description="section.description || ''"
      />

      <DsLogoBarDynamic
        v-else-if="
          (section.contentType === 'BlockLogoBar' || section.contentType === 'BlockLandingLogoBar') &&
          section.items &&
          section.items.length
        "
        :with-space="false"
        :title="section.title"
        :link="section.link"
        :items="section.items"
        centered
      >
        <template #subtitle>
          <RichText
            v-if="section.description"
            class="ds-padding-top-12"
            :data="section.description"
          />
        </template>
      </DsLogoBarDynamic>

      <BlockLandingMediaText
        v-else-if="section.contentType === 'BlockLandingMediaAndText'"
        :screen="screen"
        :image="section.image"
        :video="section.video"
        :media-position="section.mediaPosition"
        :title="section.title"
        :link="section.link"
      >
        <RichText
          v-if="section.description && Object.keys(section.description).length > 0"
          :data="section.description"
          class="ds-text"
        />
      </BlockLandingMediaText>

      <BlockLandscapeMediaThenContent
        v-else-if="section.contentType === 'BlockLandscapeMediaThenContent'"
        :screen="screen"
        :image="section.image"
        :video="section.video"
        :title="section.title"
        :description="section.description"
        :link="section.link"
      />

      <BlockContentCenteredTextOnly
        v-else-if="section.contentType === 'BlockContentCenteredTextOnly'"
        :title="section.title"
        :description="section.description"
        :link="section.link"
      />

      <div
        v-else-if="section.contentType === 'BlockSimpleQuote' && section.quote"
        class="ds-grid"
      >
        <DsQuoteSimple
          :logo="section.logo"
          :text="section.quote"
          :author="section.author"
          :extra-info="section.authorDetails"
          class="ds-grid__col ds-grid__col--12 ds-lg-grid__col--8"
        />
      </div>

      <DsQuoteGroup
        v-else-if="section.contentType === 'BlockQuoteGroup' && section.items && section.items.length > 0"
        :title="section.title"
        :description="section.description"
        :quotes="section.items"
        theme="light"
        :screen="screen"
      />

      <DsSectionGroup
        v-else-if="section.contentType === 'BlockCampaignsCards' && hasValidCardItems(section.items)"
        :theme="section.theme"
        :title="section.title"
      >
        <DsGrid
          v-if="section.items && section.items.length > 0"
          :column-width="{
            small: 12,
            medium: section.items.length % 2 === 0 ? 6 : 4,
            large: section.items.length % 2 === 0 ? 6 : 4
          }"
          :items="section.items"
        >
          <template #default="{ item }">
            <DsCardMediaWithImage
              v-if="item.image && !item.video"
              :theme="section.theme === 'grey' ? 'light' : 'grey'"
              :title="item.title"
              :text="item.text"
              :link="item.link"
              :image="item.image"
              contained
              clickable
            />

            <DsCardMediaWithVideo
              v-if="item.video"
              :video="item.video"
              :theme="section.theme === 'grey' ? 'light' : 'grey'"
              :title="item.title"
              :text="item.text"
              :link="item.link"
              :image="item.image"
              contained
            />
          </template>
        </DsGrid>
      </DsSectionGroup>

      <DsHighlightBoxMetrics
        v-else-if="section.contentType === 'BlockCampaignsMetrics'"
        :title="section.title"
        :text="section.description"
        :items="section.items"
        :theme="section.theme"
        :link="section.link"
      />

      <div
        v-else-if="section.contentType === 'BlockCampaignsCallOut' && section.image"
        class="ds-grid"
      >
        <div class="ds-grid__col">
          <DsMediaTextSection
            :title="section.title"
            :link="section.link"
            :image="section.image"
            :theme="section.theme"
            text=""
            :left="section.orientation === 'LEFT'"
          >
            <template #text>
              <RichText
                v-if="section.description"
                :data="section.description"
              />
            </template>
          </DsMediaTextSection>
        </div>
      </div>

      <DsSectionGroup
        v-else-if="section.contentType === 'BlockCampaignsMedia'"
        :with-space="false"
        :title="section.title"
        :subtitle="section.description"
        :theme="section.theme"
        :full-screen="false"
      >
        <template #default>
          <DsVideoPlayer
            v-if="section.video"
            v-bind="section.video"
            :screen="screen"
            :aria-label-play="labels.play"
            :aria-label-close="labels.close"
            data-j-video
          />
          <DsImageElement
            v-else-if="section.image"
            :image="section.image"
            :screen="screen"
            data-j-image
          />
        </template>
      </DsSectionGroup>
    </DsSection>
  </div>
</template>
<script setup>
// TODO: unify this into a single component (with EventsSections.vue)? Evaluate it with the team considering flexibility

import {
  DsLogoBarDynamic,
  DsSection,
  DsGrid,
  DsQuoteSimple,
  DsHighlightBoxMetrics,
  DsVideoPlayer,
  DsImageElement,
  DsMediaTextSection,
  DsSectionGroup,
  DsCardMediaWithImage,
  DsCardMediaWithVideo,
  DsQuoteGroup
} from 'awds'
import BlockLandingMediaText from '../../../core/components/block-landing-media-text/BlockLandingMediaText.vue'
import BlockLandscapeMediaThenContent from '../../../core/components/block-landscape-media-then-content/BlockLandscapeMediaThenContent.vue'
import BlockContentCenteredTextOnly from '../../../core/components/block-content-centered-text-only/BlockContentCenteredTextOnly.vue'
import BlockUspList from '../../../core/components/block-usp-list/BlockUspList.vue'

const { screen } = useScreen()
const { labels } = useKnowledgeHubGlobalData()

const props = defineProps({
  sections: {
    type: Array,
    default: () => []
  },
  headerTheme: {
    type: String,
    default: () => undefined
  }
})

const sectionThemes = computed(() => {
  return props.sections.map((section, index, sections) => {
    const themeAbove = sections[index - 1] ? sections[index - 1].theme || 'light' : props.headerTheme || 'light'
    const themeBelow = sections[index + 1] ? sections[index + 1].theme || 'light' : undefined
    const thisTheme = section.theme || 'light'
    return {
      id: section.id,
      sameAbove: themeAbove === thisTheme,
      sameBelow: thisTheme === themeBelow
    }
  })
})

const hasValidCardItems = (items) => {
  return items.some((item) => item.image || item.video)
}
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';
@import 'awds/foundation-mixins.scss';

.section-paddings {
  padding-top: $spacing-72;
  padding-bottom: $spacing-72;

  &.same-theme-below {
    padding-bottom: $spacing-48;
  }

  &.same-theme-above {
    padding-top: $spacing-48;
  }

  @include screen-down('medium') {
    padding-top: $spacing-48;
    padding-bottom: $spacing-48;
    &.same-theme-below {
      padding-bottom: $spacing-24;
    }
    &.same-theme-above {
      padding-top: $spacing-24;
    }
  }
}
</style>
